import type { SpinScript } from "../../MergeRoundBuilder";

// The free-spins sessions' spins. A session keeps its badges from spin to spin, so the wins land in a different place
// each time, some on cells an earlier spin badged: the badges spread over the board and build where the wins repeat.
// The sessions' lengths are the offline server's placeholder awards; the math is not settled.

/** Spin 1: a row of four L1 across the middle, 0.20; its cells x2. */
const ROW_OF_L1: SpinScript = {
  board: [
    ["L2", "L3", "L4", "H1", "L2", "L3"],
    ["L4", "H2", "L2", "L3", "L4", "L1"],
    ["L3", "L4", "H3", "L2", "H1", "L2"],
    ["L2", "L1", "L1", "L1", "L1", "L3"],
    ["H1", "L3", "L2", "L4", "L3", "L2"],
    ["L4", "L2", "L3", "H2", "L1", "L4"],
  ],
  refill: [[], [], ["L1"], [], ["H4"], []],
};

/** A dead spin. */
const DEAD_A: SpinScript = {
  board: [
    ["L1", "L2", "L2", "L3", "L1", "L2"],
    ["L3", "L1", "L4", "H2", "H4", "L4"],
    ["L4", "H3", "H1", "L4", "H1", "H4"],
    ["L1", "L3", "L1", "L3", "H3", "L1"],
    ["H2", "L3", "L1", "L4", "L3", "L1"],
    ["H3", "H1", "L2", "L3", "L2", "H1"],
  ],
  refill: [[], [], [], [], [], []],
};

/** A 2×2 L2 on the right of the middle row, covering two of its cells: ×4, 1.20. */
const SQUARE_OF_L2: SpinScript = {
  board: [
    ["L1", "L4", "L2", "L3", "L1", "H2"],
    ["L3", "L1", "H3", "L1", "L4", "L3"],
    ["H1", "L2", "L4", "L2", "L2", "L1"],
    ["L4", "L3", "L1", "L2", "L2", "L4"],
    ["L2", "L1", "H2", "L3", "L1", "L3"],
    ["L1", "H4", "L3", "L4", "L2", "L1"],
  ],
  refill: [[], [], [], ["H4"], ["L2"], []],
};

/** Two clusters at once: a 2×2 L1 in the top-left corner and a column of four L3 down the right edge, 0.20 + 0.40. */
const CORNER_AND_EDGE: SpinScript = {
  board: [
    ["L1", "L1", "L3", "H2", "L4", "L2"],
    ["L1", "L1", "L4", "L2", "H1", "H3"],
    ["L3", "L2", "H3", "L1", "L2", "L3"],
    ["H1", "L4", "L2", "L4", "L1", "L3"],
    ["L2", "L3", "L1", "H2", "L1", "L3"],
    ["L4", "H2", "L3", "L1", "L2", "L3"],
  ],
  refill: [["H4"], ["L1"], [], [], [], ["L1", "L3"]],
};

/** A dead spin. */
const DEAD_B: SpinScript = {
  board: [
    ["L1", "L3", "L2", "L4", "L1", "L2"],
    ["H1", "L4", "L2", "L3", "L4", "L1"],
    ["L2", "H2", "L1", "L3", "L2", "H2"],
    ["L1", "H4", "L4", "L1", "L1", "H1"],
    ["H2", "L1", "H1", "L3", "L4", "L2"],
    ["L1", "L1", "L2", "H3", "L3", "L1"],
  ],
  refill: [[], [], [], [], [], []],
};

/** A 2×2 L1 overlapping the corner's square by one cell (×2), climbing to L2 on its own badges (×9): 0.40 + 2.70. */
const TWO_STEP_CLIMB: SpinScript = {
  board: [
    ["L3", "L2", "L2", "L4", "L1", "H2"],
    ["L4", "L1", "L1", "H3", "L3", "L1"],
    ["H2", "L1", "L1", "H4", "L2", "L4"],
    ["L2", "H3", "L4", "L1", "H1", "L3"],
    ["L1", "H1", "L3", "L2", "L4", "L2"],
    ["L3", "L4", "L2", "H4", "L1", "L3"],
  ],
  refill: [[], ["L1", "L2"], ["H4", "L1"], [], [], []],
};

/** A dead spin. */
const DEAD_C: SpinScript = {
  board: [
    ["L1", "L2", "L4", "L4", "L2", "L2"],
    ["L3", "L3", "L2", "L3", "L4", "L3"],
    ["L1", "L3", "L2", "H4", "L3", "L2"],
    ["H2", "H1", "H3", "H2", "L2", "L3"],
    ["L3", "L1", "L4", "H1", "H2", "L2"],
    ["L4", "L2", "L1", "L3", "L3", "L4"],
  ],
  refill: [[], [], [], [], [], []],
};

/** The finale: a 2×2 L3 on the climb's lower cells and the middle row's left half (×10), climbing L3 → L4 → H1 → FS on
 * the growing badges (×14, ×18): 4.00 + 7.00 + 14.40 = 25.40, the big tier; two cells end at x6. */
const FINALE: SpinScript = {
  board: [
    ["L2", "H1", "H1", "L3", "L1", "L4"],
    ["L1", "L4", "L4", "L2", "L3", "L1"],
    ["H2", "L3", "L3", "H4", "L2", "L3"],
    ["L1", "L3", "L3", "H3", "L1", "L2"],
    ["L3", "L2", "L1", "L4", "L2", "L1"],
    ["L4", "L1", "H2", "L2", "L3", "L2"],
  ],
  refill: [[], ["L3", "H3", "L1"], ["L4", "L1", "L4", "L2"], [], [], []],
};

/** The Secret Bonus session's extra win: a column of four L4 down the left edge over the corner's badges (×4), 2.00. */
const LEFT_EDGE_L4: SpinScript = {
  board: [
    ["L4", "L2", "L1", "L3", "L2", "L1"],
    ["L4", "L3", "L2", "H2", "L1", "L3"],
    ["L4", "L1", "L3", "L1", "L4", "L2"],
    ["L4", "L2", "H3", "L2", "L3", "L1"],
    ["L1", "L3", "L1", "L4", "L2", "L4"],
    ["L3", "H2", "L2", "L1", "L3", "L2"],
  ],
  refill: [["L2", "H4"], [], [], [], [], []],
};

/** The Secret Bonus session's second extra win: a row of four L2 along the bottom right, on the edge's badge (×2), 0.60. */
const BOTTOM_ROW_L2: SpinScript = {
  board: [
    ["L1", "L3", "L4", "L1", "L3", "L4"],
    ["L3", "L1", "H2", "L4", "L1", "L3"],
    ["L2", "L4", "L1", "L3", "H3", "L1"],
    ["L1", "H1", "L3", "L1", "L4", "L3"],
    ["L4", "L3", "L1", "L4", "L1", "L4"],
    ["H3", "L1", "L2", "L2", "L2", "L2"],
  ],
  refill: [[], [], [], ["H1"], [], ["L2"]],
};

/** A dead spin (the Secret Bonus session's). */
const DEAD_D: SpinScript = {
  board: [
    ["L1", "L1", "H3", "H4", "L2", "L4"],
    ["L1", "L4", "L3", "L1", "L3", "L3"],
    ["H4", "L4", "L3", "H2", "L2", "L3"],
    ["L4", "L1", "H4", "L4", "L3", "L1"],
    ["L2", "L3", "L1", "L3", "L1", "L1"],
    ["L1", "L1", "H2", "L3", "H2", "L2"],
  ],
  refill: [[], [], [], [], [], []],
};

/** A dead spin (the Secret Bonus session's). */
const DEAD_E: SpinScript = {
  board: [
    ["L3", "L4", "L3", "L4", "H2", "H1"],
    ["L1", "L3", "H4", "L1", "L2", "L1"],
    ["H1", "L2", "L2", "L3", "H2", "L3"],
    ["H4", "L4", "L3", "H3", "L3", "L2"],
    ["L1", "L2", "L2", "L3", "L2", "L1"],
    ["L3", "L2", "H2", "L2", "L2", "L3"],
  ],
  refill: [[], [], [], [], [], []],
};

/**
 * The free-spins session (the trigger's and the Bonus Buy's), eight spins: five wins and three dead spins, the finale's
 * climb on the badges built before it. It pays 30.50.
 */
export const FREE_SPIN_SCRIPTS: readonly SpinScript[] = [ROW_OF_L1, DEAD_A, SQUARE_OF_L2, CORNER_AND_EDGE, DEAD_B, TWO_STEP_CLIMB, DEAD_C, FINALE];

/**
 * The Secret Bonus session (decision 51): more free spins than the Bonus Buy — twelve, the same eight with two more wins
 * on the board's edges (on cells the corner and the edge badged) and two more dead spins, the finale still last. Its
 * extra wins touch none of the finale's cells, so the finale pays as in the eight. It pays 33.10.
 */
export const SECRET_BONUS_SPIN_SCRIPTS: readonly SpinScript[] = [
  ROW_OF_L1, DEAD_A, SQUARE_OF_L2, CORNER_AND_EDGE, LEFT_EDGE_L4, DEAD_B, TWO_STEP_CLIMB, DEAD_D, BOTTOM_ROW_L2, DEAD_C, DEAD_E, FINALE,
];
